import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BusinessIcon from '@mui/icons-material/Business';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import DatosMaestrosService from '../../services/datosMaestrosService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ComboboxDatosMaestros from '../../components/ComboboxDatosMaestros';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import TarjetaTurnoAnses from '../../components/TarjetaTurnoAnses';
import ExpedientesAnsesTramite, { enSeguimientoDeExpediente } from '../../components/ExpedientesAnsesTramite';
import RegularidadMinusvalia from '../../components/RegularidadMinusvalia';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import TarjetaCitacion from '../../components/recepcion/CitacionEstudio';
import turneroStyles from '../../style/pages/Turnero.module.css';
import {
    getTramiteMinusvaliaById, actualizarTramiteMinusvalia, actualizarComentariosMinusvalia,
    avanzarEstadoMinusvalia, getRegularidadMinusvalia,
} from '../../services/minusvaliaService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { obtenerTurnoAnsesPorTramite } from '../../services/turnoAnsesService';
import { apellidoNombre } from '../../utils/nombrePersona';
import EtiquetaTramite from '../../components/EtiquetaTramite';

const UMBRAL_DICTAMEN = 33;

const formatearFechaEvento = (fechaString) => {
    if (!fechaString) return '';
    const [y, m, d] = fechaString.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

// Mini-formulario por transición (patrón RTI/ART/Reajuste/Ganancias). El paso a "Turno Anses" no
// tiene botón acá: lo dispara la propia TarjetaTurnoAnses al sacar un turno de tipo PRESENTACION.
const TRANSICIONES_BASE = {
    'Ver regularidad': [
        {
            destino: 'NO_CUMPLE',
            label: 'Cerrar: no cumple regularidad',
            icon: 'bi-x-octagon',
            variant: 'outline-secondary',
            campos: [
                { name: 'motivoNoCumple', label: 'Motivo (por qué no cumple la regularidad)', type: 'textarea' },
            ],
        },
    ],
};

const TRANSICION_CANCELAR = {
    destino: 'CANCELADO',
    label: 'Cancelar trámite',
    icon: 'bi-slash-circle-fill',
    variant: 'outline-secondary',
    campos: [
        { name: 'comentario', label: 'Motivo de la cancelación', type: 'textarea' },
    ],
};

const TRANSICION_REACTIVAR = {
    destino: 'VER_REGULARIDAD',
    label: 'Reactivar trámite',
    icon: 'bi-arrow-counterclockwise',
    variant: 'outline-primary',
    campos: [
        { name: 'comentario', label: 'Motivo de la reactivación', type: 'textarea' },
    ],
};

const TRANSICIONES_POR_ESTADO = {
    ...Object.fromEntries(
        Object.entries(TRANSICIONES_BASE).map(([estado, transiciones]) => [
            estado,
            [...transiciones, TRANSICION_CANCELAR],
        ])
    ),
    'Turno Anses': [TRANSICION_CANCELAR],
    'Cancelado': [TRANSICION_REACTIVAR],
};

function MiniFormularioTransicion({ idTramite, transicion, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);

    const handleChange = (name, value) => setValores(prev => ({ ...prev, [name]: value }));

    const guardar = async () => {
        setGuardando(true);
        try {
            const payload = { destino: transicion.destino, ...valores };
            const actualizado = await avanzarEstadoMinusvalia(idTramite, payload);
            onGuardado(actualizado);
            toast.success('Estado actualizado correctamente');
            setAbierto(false);
            setValores({});
        } catch (err) {
            console.error('Error al avanzar de estado:', err);
            toast.error(err.response?.data || 'No se pudo avanzar de estado');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className={`btn btn-${transicion.variant || 'primary'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`} onClick={() => setAbierto(true)}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'}`}></i>{transicion.label}
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold mb-3" style={{ color: '#4338ca' }}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'} me-2`}></i>{transicion.label}
            </h5>
            <div className="row g-3">
                {transicion.campos.map(campo => (
                    <div key={campo.name} className={campo.type === 'textarea' ? 'col-12' : 'col-md-6'}>
                        <label className="form-label fw-semibold">{campo.label}</label>
                        {campo.type === 'textarea' ? (
                            <textarea className="form-control shadow-sm" rows={3}
                                value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                        ) : (
                            <div className="input-group">
                                {campo.icon && <span className="input-group-text bg-light"><i className={`bi ${campo.icon}`}></i></span>}
                                <input type={campo.type} className="form-control shadow-sm"
                                    value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}
                    style={{ background: 'var(--primary)', border: 'none' }}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Confirmar</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => { setAbierto(false); setValores({}); }} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

/** Formulario propio de "Registrar turno de junta médica": fecha, hora, sede (con catálogo de
 * lugares, dando de alta la sede si es nueva, mismo patrón que RTI) y número de expediente. Un
 * solo PATCH /estado: el backend crea el evento de agenda "Junta Médica" y cambia el estado en la
 * misma transacción (a diferencia de RTI, acá el frontend no crea el evento por su cuenta).
 *
 * El número de expediente se precarga solo, del que ya se cargó al registrar INGRESADO en el
 * turno ANSES (TarjetaTurnoAnses): es el mismo expediente, y pedirlo de nuevo era doble carga del
 * mismo dato. Sigue siendo editable por si hay que corregirlo. */
function FormularioJuntaMedica({ idTramite, onGuardado, onJuntaRegistrada }) {
    const [abierto, setAbierto] = useState(false);
    const [lugares, setLugares] = useState([]);
    const [datos, setDatos] = useState({ fecha: '', hora: '', sede: '', nroExpediente: '', observaciones: '' });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!abierto) return;
        DatosMaestrosService.getLugares()
            .then((resp) => setLugares(resp.data || []))
            .catch((err) => console.error('Error al cargar lugares:', err));
    }, [abierto]);

    useEffect(() => {
        if (!abierto) return;
        obtenerTurnoAnsesPorTramite(idTramite)
            .then((turno) => {
                // idTramite llega de useParams() como string ("42"); el idTramite de la
                // presentación viaja como number en el JSON — sin el String() de los dos lados,
                // la comparación estricta nunca matcheaba y el expediente no se precargaba nunca.
                const propia = (turno?.presentaciones || [])
                    .find((p) => String(p.idTramite) === String(idTramite));
                const expediente = propia?.numeroExpediente;
                if (expediente) {
                    setDatos((prev) => (prev.nroExpediente ? prev : { ...prev, nroExpediente: expediente }));
                }
            })
            .catch((err) => console.error('Error al buscar el expediente del turno ANSES:', err));
    }, [abierto, idTramite]);

    const crearLugarSiNoExiste = async (nombreLugar) => {
        const nombreTrim = (nombreLugar || '').trim();
        if (!nombreTrim) return;
        const existente = lugares.find(l => l.nombre && l.nombre.toLowerCase() === nombreTrim.toLowerCase());
        if (existente) return;
        try {
            await DatosMaestrosService.createLugar({
                nombre: nombreTrim,
                tipo: 'Oficina',
                direccion: '',
                telefono: '',
                descripcion: 'Creado automáticamente desde trámite de Minusvalía',
            });
        } catch (err) {
            console.error('Error al crear lugar automáticamente:', err);
        }
    };

    const guardar = async () => {
        if (!datos.fecha || !datos.hora || !datos.sede || !datos.nroExpediente.trim()) {
            toast.error('Debe completar fecha, hora, sede y número de expediente de la Junta Médica.');
            return;
        }
        setGuardando(true);
        try {
            await crearLugarSiNoExiste(datos.sede);
            const actualizado = await avanzarEstadoMinusvalia(idTramite, {
                destino: 'JUNTA_MEDICA',
                fechaJunta: datos.fecha,
                horaJunta: datos.hora,
                sedeJunta: datos.sede,
                nroExpediente: datos.nroExpediente,
                observacionesJunta: datos.observaciones,
            });
            onGuardado(actualizado);
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-minusvalia' } }));
            toast.success('Junta Médica registrada correctamente');
            // La junta médica queda como evento (no toca turno_anses: no es una ida a ANSES). Si
            // además hace falta que alguien del estudio acompañe, se ofrece acá un turno de
            // Recepción común (tipo_turno "JUNTA_MEDICA", ya existe en el catálogo — V175), separado
            // y opcional, precargado con la misma fecha/hora que se acaba de cargar.
            if (onJuntaRegistrada) {
                onJuntaRegistrada({ fecha: datos.fecha, hora: datos.hora });
            }
            setAbierto(false);
            setDatos({ fecha: '', hora: '', sede: '', nroExpediente: '', observaciones: '' });
        } catch (err) {
            console.error('Error al registrar la Junta Médica:', err);
            toast.error(err.response?.data || 'No se pudo registrar la Junta Médica.');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                onClick={() => setAbierto(true)} style={{ background: 'var(--primary)', border: 'none' }}>
                <i className="bi bi-calendar-heart"></i>Registrar turno de junta médica
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold mb-3" style={{ color: '#4338ca' }}><i className="bi bi-calendar-heart me-2"></i>Registrar turno de junta médica</h5>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Fecha Junta <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={datos.fecha} onChange={(e) => setDatos({ ...datos, fecha: e.target.value })} />
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Hora Junta <span className="text-danger">*</span></label>
                    <input type="time" className="form-control" value={datos.hora} onChange={(e) => setDatos({ ...datos, hora: e.target.value })} />
                </div>
                <div className="col-12">
                    <ComboboxDatosMaestros
                        opciones={lugares.filter(l => ['Oficina', 'Dependencia', 'Sala'].includes(l.tipo) || !l.tipo)}
                        valor={datos.sede}
                        onChange={(valor) => setDatos({ ...datos, sede: valor })}
                        tipo="lugar"
                        tipoLugar="Oficina"
                        placeholder="Seleccione o escriba un lugar..."
                        label="Sede / Lugar"
                        required
                        icon={BusinessIcon}
                    />
                </div>
                <div className="col-12">
                    <label className="form-label fw-semibold">N° de expediente <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={datos.nroExpediente} onChange={(e) => setDatos({ ...datos, nroExpediente: e.target.value })} />
                </div>
                <div className="col-12">
                    <label className="form-label fw-semibold">Observaciones</label>
                    <textarea className="form-control shadow-sm" rows={3} value={datos.observaciones}
                        onChange={(e) => setDatos({ ...datos, observaciones: e.target.value })} />
                </div>
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary rounded-pill px-4" onClick={guardar} disabled={guardando}
                    style={{ background: 'var(--primary)', border: 'none' }}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : 'Confirmar Junta Médica'}
                </button>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setAbierto(false)} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

/** Formulario propio de "Registrar dictamen": porcentaje de incapacidad (con aviso en vivo de a
 * qué cierre resuelve) + fecha. El backend decide solo si cierra favorable (>=33%) o
 * desfavorable. */
function FormularioDictamen({ idTramite, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [porcentaje, setPorcentaje] = useState('');
    const [fecha, setFecha] = useState('');
    const [comentario, setComentario] = useState('');
    const [guardando, setGuardando] = useState(false);

    const guardar = async () => {
        const valorNumerico = Number(porcentaje);
        if (porcentaje === '' || Number.isNaN(valorNumerico)) {
            toast.error('Debe indicar el porcentaje de incapacidad dictaminado.');
            return;
        }
        if (valorNumerico < 0 || valorNumerico > 100) {
            toast.error('El porcentaje de incapacidad debe estar entre 0 y 100.');
            return;
        }
        setGuardando(true);
        try {
            const actualizado = await avanzarEstadoMinusvalia(idTramite, {
                destino: 'DICTAMEN',
                porcentajeIncapacidad: valorNumerico,
                fechaDictamen: fecha || null,
                comentario,
            });
            onGuardado(actualizado);
            toast.success('Dictamen registrado correctamente');
            setAbierto(false);
            setPorcentaje('');
            setFecha('');
            setComentario('');
        } catch (err) {
            console.error('Error al registrar el dictamen:', err);
            toast.error(err.response?.data || 'No se pudo registrar el dictamen.');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                onClick={() => setAbierto(true)} style={{ background: 'var(--primary)', border: 'none' }}>
                <i className="bi bi-clipboard2-check"></i>Registrar dictamen
            </button>
        );
    }

    const numero = porcentaje === '' ? null : Number(porcentaje);
    const previsto = numero == null || Number.isNaN(numero)
        ? null
        : (numero >= UMBRAL_DICTAMEN ? 'favorable' : 'desfavorable');

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold mb-3" style={{ color: '#4338ca' }}><i className="bi bi-clipboard2-check me-2"></i>Registrar dictamen</h5>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Porcentaje de incapacidad <span className="text-danger">*</span></label>
                    <div className="input-group">
                        <input type="number" step="0.01" min="0" max="100" className="form-control" value={porcentaje}
                            onChange={(e) => setPorcentaje(e.target.value)} />
                        <span className="input-group-text">%</span>
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Fecha de dictamen</label>
                    <input type="date" className="form-control" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                </div>
                <div className="col-12">
                    <label className="form-label fw-semibold">Comentario (opcional)</label>
                    <textarea className="form-control shadow-sm" rows={3} value={comentario} onChange={(e) => setComentario(e.target.value)} />
                </div>
            </div>
            {previsto && (
                <div className={`alert border-0 rounded-3 mt-3 mb-0 ${previsto === 'favorable' ? 'alert-success' : 'alert-danger'}`}>
                    Con {numero}% se cierra <strong>{previsto}</strong> (umbral {UMBRAL_DICTAMEN}%)
                </div>
            )}
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary rounded-pill px-4" onClick={guardar} disabled={guardando}
                    style={{ background: 'var(--primary)', border: 'none' }}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : 'Confirmar dictamen'}
                </button>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setAbierto(false)} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

function SeccionAvanzarEstado({ idTramite, nombreEstadoActual, onAvanzado, onJuntaRegistrada }) {
    const transiciones = TRANSICIONES_POR_ESTADO[nombreEstadoActual] || [];
    const esJuntaMedica = nombreEstadoActual === 'Junta médica';
    if (transiciones.length === 0 && !esJuntaMedica) {
        return (
            <div className="alert alert-secondary border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                <i className="bi bi-flag-fill"></i>Este trámite está en una etapa final del ciclo de estados.
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-3">
            {esJuntaMedica && <FormularioDictamen idTramite={idTramite} onGuardado={onAvanzado} />}
            {nombreEstadoActual === 'Turno Anses' && (
                <FormularioJuntaMedica idTramite={idTramite} onGuardado={onAvanzado} onJuntaRegistrada={onJuntaRegistrada} />
            )}
            {transiciones.map(t => (
                <MiniFormularioTransicion key={t.destino} idTramite={idTramite} transicion={t} onGuardado={onAvanzado} />
            ))}
        </div>
    );
}

function VistaDetalladaTramiteMinusvalia() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal, showConfirm } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);
    // Datos de la junta médica recién registrada, para precargar el turno de Recepción opcional
    // que se ofrece a continuación (ver handleJuntaRegistrada). Null = el modal de turno se abre
    // "en blanco" (el botón "Crear turno" del header).
    const [prefillTurnoJunta, setPrefillTurnoJunta] = useState(null);
    // Recién registrada la junta y aceptado citar: la tarjeta de citación abre el turnero sola.
    const [citarJunta, setCitarJunta] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);

    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const [regularidad, setRegularidad] = useState(null);
    const [cargandoRegularidad, setCargandoRegularidad] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteMinusvaliaById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Minusvalía:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteMinusvaliaById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM/Reajuste/Ganancias)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    // Regularidad de aportes: solo importa mientras el trámite está en "Ver regularidad".
    useEffect(() => {
        const cuil = tramite?.cliente?.cuil;
        if (!tramite || tramite.nombreEstadoActual !== 'Ver regularidad' || !cuil) {
            return;
        }
        let activo = true;
        setCargandoRegularidad(true);
        getRegularidadMinusvalia(cuil)
            .then((data) => { if (activo) setRegularidad(data); })
            .catch((err) => { console.error('Error al calcular regularidad de minusvalía:', err); if (activo) setRegularidad(null); })
            .finally(() => { if (activo) setCargandoRegularidad(false); });
        return () => { activo = false; };
    }, [tramite]);

    const formatLocalFecha = (fechaInput) => {
        if (!fechaInput) return '';
        if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
        const d = new Date(fechaInput);
        if (Number.isNaN(d.getTime())) return '';
        return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-minusvalia' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosMinusvalia(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            patologia: tramite.patologia || '',
            cuota: tramite.cuota || '',
            ddjjSalud: Boolean(tramite.ddjjSalud),
            aportesProvincia: Boolean(tramite.aportesProvincia),
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async () => {
        setGuardandoDatos(true);
        try {
            await actualizarTramiteMinusvalia(idTramite, {
                ...tramiteEdit,
                organismo: tramiteEdit.organismo || null,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            console.error('Error al guardar datos:', error);
            toast.error(error.response?.data || 'No se pudieron guardar los datos');
        } finally {
            setGuardandoDatos(false);
        }
    };

    const handleAvanzarEstado = (tramiteActualizado) => {
        setTramite(tramiteActualizado);
    };

    // Ofrece, sin obligar, un turno de Recepción para la junta médica recién cargada. Es un turno
    // común del estudio (tipo_turno "JUNTA_MEDICA", ya existe en el catálogo desde V175), no un
    // turno_anses: la junta no es una ida a ANSES. Se precarga con la misma fecha/hora que ya se
    // cargó para no volver a tipearla.
    // Igual que en RTI (V254): la citación queda vinculada a la junta, así el recordatorio de
    // WhatsApp le dice al cliente a qué hora venir al estudio y no solo la hora de la junta.
    const handleJuntaRegistrada = () => {
        showConfirm(
            '¿Citamos al cliente en el estudio antes de la Junta Médica? Se sugiere una hora antes; el recordatorio de WhatsApp le va a decir las dos horas.',
            async () => { await recargarEventos(); setCitarJunta(true); },
            'Junta Médica registrada',
            'primary'
        );
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#4f46e5' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    // La más nueva: si la junta se reprogramó hay más de un evento.
    const eventoJunta = eventos.filter(e => e.tipoEvento === 'Junta Médica')
        .sort((a, b) => (b.idEvento || 0) - (a.idEvento || 0))[0];
    const tieneSeguimiento = Boolean(eventoJunta) || tramite.fechaDictamen != null || tramite.porcentajeIncapacidad != null || tramite.motivoNoCumple;
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold mb-0" style={{ color: '#4338ca' }}>Detalles del Trámite de Minusvalía</h2>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
                            MINUSVALIA
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <button
                        className="btn d-flex align-items-center gap-2 rounded-pill"
                        style={{ border: '1px solid #4f46e5', color: '#4f46e5' }}
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            <BannerEstadoFinal
                idTramite={tramite.idTramite}
                nombreEstadoActual={tramite.nombreEstadoActual}
            />

            {/* Estado del ciclo: cada avance dispara el mini-formulario de esa etapa */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
                            {tramite.nombreEstadoActual || 'Ver regularidad'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setModalHistorialAbierto(true)}>
                        <i className="bi bi-clock-history me-1"></i>Ver historial
                    </button>
                </div>

                {tramite.nombreEstadoActual === 'Ver regularidad' && (
                    <div className="mb-3">
                        <RegularidadMinusvalia regularidad={regularidad} cargando={cargandoRegularidad} cuil={tramite?.cliente?.cuil} />
                    </div>
                )}

                <SeccionAvanzarEstado idTramite={idTramite} nombreEstadoActual={tramite.nombreEstadoActual} onAvanzado={handleAvanzarEstado} onJuntaRegistrada={handleJuntaRegistrada} />

                {/* Turno en ANSES: lo maneja el módulo de turnos ANSES. Sacar un turno de
                    presentación es lo que avanza el trámite a "Turno Anses"; la resolución de
                    ANSES NO cierra el trámite acá (lo cierra el dictamen de la junta médica). */}
                <div className="border-top mt-4 pt-3">
                    <h6 className="fw-bold text-secondary mb-3">
                        <i className="bi bi-building me-2"></i>Turno en ANSES
                    </h6>
                    <TarjetaTurnoAnses
                        idTramite={tramite.idTramite}
                        nombreArea="este trámite de Minusvalía"
                        nombreEstadoActual={tramite.nombreEstadoActual}
                        onCambio={recargarTramite}
                        onEstadoCambiado={recargarTramite}
                        ofreceResolucion={false}
                    />
                    {enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}
                </div>
            </div>

            {/* Nota editable de comentarios (con firma, igual que el resto de los módulos) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Minusvalía"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: '#4338ca' }}>Eventos del Trámite</h4>
                            <button
                                className="btn d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                style={{ border: '1px solid #4f46e5', color: '#4f46e5' }}
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" style={{ color: '#4f46e5' }} role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold mb-0" style={{ color: '#4338ca' }}>{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge rounded-pill" style={{ backgroundColor: '#4f46e5' }}>{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2" style={{ whiteSpace: 'pre-line' }}>{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="fw-semibold" style={{ color: '#4338ca' }}>Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Datos del Trámite + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#4338ca' }}>
                            <i className="bi bi-file-earmark-text me-2"></i>
                            Datos del Trámite
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Organismo:</strong> {tramite.organismo || 'Sin especificar'}</p>
                                <p><strong>Patología:</strong> {tramite.patologia || 'N/A'}</p>
                                <p><strong>Cuota:</strong> {tramite.cuota || 'N/A'}</p>
                                <p><strong>DDJJ de salud:</strong> {tramite.ddjjSalud ? 'Sí' : 'No'}</p>
                                <p><strong>Aporta en provincia:</strong> {tramite.aportesProvincia ? 'Sí' : 'No'}</p>
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn rounded-pill px-4 fw-semibold" style={{ border: '1px solid #4f46e5', color: '#4f46e5' }} onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Organismo</label>
                                    <select className="form-select" value={tramiteEdit.organismo}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, organismo: e.target.value }))}>
                                        <option value="">Sin especificar</option>
                                        <option value="PROVINCIA">Provincia</option>
                                        <option value="ANSES">ANSES</option>
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Patología</label>
                                    <input type="text" className="form-control" value={tramiteEdit.patologia}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, patologia: e.target.value }))} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Cuota</label>
                                    <input type="text" className="form-control" value={tramiteEdit.cuota}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, cuota: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-3 mb-3">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="edit-ddjjSalud" checked={tramiteEdit.ddjjSalud}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, ddjjSalud: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor="edit-ddjjSalud">DDJJ de salud presentada</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="edit-aportesProvincia" checked={tramiteEdit.aportesProvincia}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, aportesProvincia: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor="edit-aportesProvincia">Aporta en provincia</label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ backgroundColor: '#4f46e5' }} onClick={handleGuardarDatos} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expedientes de ANSES: acá, debajo de los datos del trámite y del cliente; arriba solo
                mientras el trámite está en "Seguimiento de Expediente". */}
            {!enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Regularidad de minusvalía: siempre visible (no solo en "Ver regularidad") */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <RegularidadMinusvalia regularidad={regularidad} cargando={cargandoRegularidad} cuil={tramite?.cliente?.cuil} />
                </div>
            </div>

            {/* Seguimiento del trámite: cada dato de su fuente real (junta médica del evento de
                agenda, dictamen de las columnas del trámite, turno ANSES de su propia tarjeta más
                arriba), sin duplicar nada. */}
            {tieneSeguimiento && (
                <div className="row g-4 mt-1">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 rounded-4 p-4">
                            <h4 className="fw-bold mb-3" style={{ color: '#4338ca' }}><i className="bi bi-signpost-split me-2"></i>Seguimiento del Trámite</h4>
                            <div className="row g-2">
                                {eventoJunta && (
                                    <>
                                        <div className="col-md-6"><strong>Junta médica:</strong> {formatearFechaEvento(eventoJunta.fecha)} {eventoJunta.horaInicio}</div>
                                        {eventoJunta.descripcion && (
                                            <div className="col-12 text-muted small" style={{ whiteSpace: 'pre-line' }}>{eventoJunta.descripcion}</div>
                                        )}
                                        <div className="col-12">
                                            <TarjetaCitacion
                                                evento={eventoJunta} tipo="JUNTA_MEDICA" cliente={cliente}
                                                abrirAlMontar={citarJunta} onAbierto={() => setCitarJunta(false)}
                                            />
                                        </div>
                                    </>
                                )}
                                {tramite.fechaDictamen && <div className="col-md-6"><strong>Fecha de dictamen:</strong> {tramite.fechaDictamen}</div>}
                                {tramite.porcentajeIncapacidad != null && (
                                    <div className="col-md-6">
                                        <strong>Porcentaje de incapacidad:</strong>{' '}
                                        <span className={`badge rounded-pill ${Number(tramite.porcentajeIncapacidad) >= UMBRAL_DICTAMEN ? 'bg-success' : 'bg-danger'}`}>
                                            {tramite.porcentajeIncapacidad}%
                                        </span>
                                    </div>
                                )}
                                {tramite.motivoNoCumple && (
                                    <div className="col-12"><strong>Motivo de no cumplir regularidad:</strong> {tramite.motivoNoCumple}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Otros documentos */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4 className="fw-bold mb-0" style={{ color: '#4338ca' }}><i className="bi bi-paperclip me-2"></i>Otros documentos</h4>
                            <label className="btn btn-sm rounded-pill mb-0" style={{ border: '1px solid #4f46e5', color: '#4f46e5' }}>
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivos.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivos.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text" style={{ fontSize: '1.25rem', color: '#4f46e5' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="MINUSVALIA"
            />

            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="MINUSVALIA"
            />

            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => { setMostrarModalTurno(false); setPrefillTurnoJunta(null); }}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            // Sin celdaSel a propósito: esa prop deja la fecha/hora fijas y sin
                            // selector (pensada para "hiciste clic en esta celda de la grilla"), y
                            // acá la fecha/hora de la junta es solo una sugerencia editable — puede
                            // no coincidir con un bloque exacto del turnero (ej. "14:46").
                            celdaSel={null}
                            fechaSugerida={prefillTurnoJunta?.fecha}
                            horaSugerida={prefillTurnoJunta?.hora}
                            // El cliente ya es de la carpeta (está en esta ficha): se vincula directo,
                            // igual que si se lo hubiera buscado por CUIL/nombre en el modal.
                            clienteInicial={cliente}
                            valoresIniciales={{
                                tipoTramite: prefillTurnoJunta ? 'JUNTA_MEDICA' : 'MINUSVALIA',
                                // Ya es cliente del estudio (tiene esta carpeta abierta): no puede
                                // ser su primera vez, así que los dos campos se marcan consistentes.
                                tieneCarpeta: true,
                                esPrimeraVez: 'NO',
                            }}
                            onCerrar={() => { setMostrarModalTurno(false); setPrefillTurnoJunta(null); }}
                            onCreado={() => {
                                setMostrarModalTurno(false);
                                setPrefillTurnoJunta(null);
                                toast.success('Turno creado correctamente');
                            }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteMinusvalia;
